import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {AuthService} from '@core/services/auth.service';
import {ConsultaSei} from '@core/models/processo-sei';

export type EstadoSei = 'verificando' | 'logado' | 'deslogado' | 'sem-extensao' | 'antiga' | 'erro';

/**
 * Conexão com a extensão do Chrome "SisNot ↔ SEI" (abre o processo no SEI do
 * usuário). A extensão marca o <html> com data-sisnot-sei e responde ao ping
 * por postMessage; qualquer elemento com data-sei-numero vira um clique que
 * abre o processo (quem trata o clique é a extensão).
 *
 * Igual ao SIGEP: só para quem está logado e tem a "Integração com o SEI"
 * liberada no Admin > Usuários (permissão integracao_sei). O número só vira
 * link com o SEI conectado (bolinha verde): aí o <html> leva
 * data-sisnot-sei-liberado e, sem ele, a extensão fica desligada no SisNot.
 */
@Injectable({
  providedIn: 'root',
})
export class SeiService {
  /** Versão da extensão no zip de src/assets/extensao (muda junto com o manifest.json dela). */
  static readonly VERSAO_ATUAL = '1.8.3';
  static readonly MINHA_AREA = 'https://minhaarea.sp.gov.br/plataformasp';
  private static readonly NUMERO_SEI = /\d{3}\.\d{7,8}\/\d{4}-\d{2}/;

  private auth = inject(AuthService);
  private permissoes = toSignal(this.auth.getPermissions(), {initialValue: this.auth.getPermissionsValue()});

  /** Versão da extensão instalada, ou null sem a extensão. */
  readonly extensao = signal<string | null>(document.documentElement.getAttribute('data-sisnot-sei'));
  /** Logado no SisNot e com a integração com o SEI liberada. */
  readonly liberado = computed(() => this.auth.isAuthenticated() && !!this.permissoes()?.includes('integracao_sei'));
  /** Sessão do SEI neste navegador, segundo a extensão. */
  readonly estado = signal<EstadoSei>('verificando');
  /** Número SEI clicável: liberado e com o SEI conectado neste navegador. */
  readonly ativo = computed(() => this.liberado() && this.estado() === 'logado');
  readonly desatualizada = computed(() => {
    const v = this.extensao();
    return !!v && SeiService.compararVersao(v, SeiService.VERSAO_ATUAL) < 0;
  });

  private pedido = 0;

  constructor() {
    effect(() => document.documentElement.toggleAttribute('data-sisnot-sei-liberado', this.ativo()));

    // a extensão pode carregar antes ou depois do SisNot: escuta o "pronta" e pergunta
    window.addEventListener('message', (ev: MessageEvent) => {
      if (ev.source !== window || ev.origin !== window.location.origin) return;
      if (ev.data?.tipo === 'sisnot-sei:pronta') {
        const novaNaPagina = !this.extensao();
        this.extensao.set(String(ev.data.versao || '1'));
        if (novaNaPagina && this.liberado()) this.verificar();
      } else if (ev.data?.tipo === 'sisnot-sei:progresso') {
        this.andamento.set(String(ev.data.texto || ''));
      } else if (ev.data?.tipo === 'sisnot-sei:resposta' && ev.data.pedido === 'sisnot-sei:verificar' && ev.data.id === this.pedido) {
        this.estado.set(!ev.data.ok ? 'erro' : ev.data.logado ? 'logado' : 'deslogado');
      }
    });
    window.postMessage({tipo: 'sisnot-sei:ping'}, window.location.origin);
  }

  /** Pergunta à extensão se o usuário está logado no SEI (a página sozinha não vê a sessão de outro site). */
  verificar() {
    if (!this.extensao()) {
      this.estado.set('sem-extensao');
      return;
    }
    // extensão antiga (antes da 1.3.0) não responde: pede pra atualizar
    if (SeiService.compararVersao(this.extensao()!, '1.3.0') < 0) {
      this.estado.set('antiga');
      return;
    }
    // sem voltar pra "verificando": o link não some a cada conferência
    const id = ++this.pedido;
    window.postMessage({tipo: 'sisnot-sei:verificar', id}, window.location.origin);
    setTimeout(() => {
      if (id === this.pedido && this.estado() === 'verificando') this.estado.set('erro');
    }, 15000);
  }

  /**
   * Bot SisNot ↔ SEI: em que unidade(s) do SEI o processo está aberto (a extensão
   * pesquisa com a sessão do usuário, sem abrir aba).
   */
  async consultar(numero: string): Promise<ConsultaSei> {
    const r = await this.pedir('sisnot-sei:consultar', {numero}, 60000);
    return {status: r['status'] || 'erro', unidades: r['unidades'] || [], erro: r['erro'], trecho: r['trecho'], unidadeSessao: r['unidadeSessao']};
  }

  /**
   * Bot SisNot ↔ SEI: números de todos os processos da caixa de uma unidade do SEI.
   * A extensão troca de unidade num quadro escondido e volta pra unidade original
   * no fim (a unidade é da sessão do SEI inteira). Pode levar alguns minutos.
   * Sem unidade: a caixa da unidade em que o usuário está, sem trocar.
   * `vermelhos`: os processos ainda não visualizados (em vermelho) na caixa.
   */
  async lerCaixa(unidade: string): Promise<{ok: boolean; unidade: string; numeros: string[]; vermelhos: string[]; erro?: string}> {
    const r = await this.pedir('sisnot-sei:ler_caixa', {unidade}, 15 * 60000);
    return {ok: !!r['ok'], unidade: r['unidade'] || unidade, numeros: r['numeros'] || [], vermelhos: r['vermelhos'] || [], erro: r['erro']};
  }

  /** Última mensagem de andamento da extensão (leitura da caixa: "página 3 de 10…"). */
  readonly andamento = signal('');

  // pedido à extensão por postMessage e a resposta dela (mesmo id)
  private pedir(tipo: string, dados: Record<string, unknown>, ms: number): Promise<Record<string, any>> {
    return new Promise(ok => {
      const id = `bot-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const ouvir = (ev: MessageEvent) => {
        if (ev.source !== window || ev.data?.tipo !== 'sisnot-sei:resposta' || ev.data.id !== id) return;
        window.removeEventListener('message', ouvir);
        clearTimeout(tempo);
        ok(ev.data);
      };
      const tempo = setTimeout(() => {
        window.removeEventListener('message', ouvir);
        ok({ok: false, status: 'erro', erro: 'a extensão não respondeu'});
      }, ms);
      window.addEventListener('message', ouvir);
      window.postMessage({...dados, tipo, id}, window.location.origin);
    });
  }

  /** Número de processo SEI dentro do texto (ex.: "134.00054956/2026-16"), ou null. */
  numero(texto: string | null | undefined): string | null {
    return texto?.match(SeiService.NUMERO_SEI)?.[0] ?? null;
  }

  private static compararVersao(a: string, b: string): number {
    const pa = a.split('.').map(n => parseInt(n, 10) || 0), pb = b.split('.').map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i++) {
      if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
    }
    return 0;
  }
}
